import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import './style.scss';
import {
    COLLECTION_READY_TIMEOUT_MS,
    getCollectionEmbedUrl,
    isTrustedCollectionReadyMessage,
} from './collectionUrl';

const DEFAULT_HEADER_HEIGHT = 64;

const Collection = () => {
    const iframeRef = useRef(null);
    const [attempt, setAttempt] = useState(0);
    const [frameLoaded, setFrameLoaded] = useState(false);
    const [phase, setPhase] = useState('loading');
    const [headerHeight, setHeaderHeight] = useState(DEFAULT_HEADER_HEIGHT);
    const collectionUrl = useMemo(() => getCollectionEmbedUrl(), []);
    const collectionOrigin = useMemo(() => new URL(collectionUrl).origin, [collectionUrl]);

    useLayoutEffect(() => {
        const header = document.querySelector('.tou');
        if (!header) return undefined;

        const updateHeaderHeight = () => {
            const nextHeight = Math.ceil(header.getBoundingClientRect().height);
            if (nextHeight > 0) setHeaderHeight(nextHeight);
        };

        updateHeaderHeight();

        if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(updateHeaderHeight);
            resizeObserver.observe(header);
            return () => resizeObserver.disconnect();
        }

        window.addEventListener('resize', updateHeaderHeight);
        return () => window.removeEventListener('resize', updateHeaderHeight);
    }, []);

    useEffect(() => {
        setPhase('loading');
        setFrameLoaded(false);

        const timeoutId = window.setTimeout(() => {
            setPhase((currentPhase) => currentPhase === 'ready' ? currentPhase : 'timeout');
        }, COLLECTION_READY_TIMEOUT_MS);

        const handleMessage = (event) => {
            if (!isTrustedCollectionReadyMessage({
                event,
                frameWindow: iframeRef.current?.contentWindow,
                expectedOrigin: collectionOrigin,
            })) return;

            window.clearTimeout(timeoutId);
            setPhase('ready');
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.clearTimeout(timeoutId);
            window.removeEventListener('message', handleMessage);
        };
    }, [attempt, collectionOrigin]);

    const retry = () => setAttempt((currentAttempt) => currentAttempt + 1);
    // The embedded Collection already has its own layout-matched skeletons.
    // Reveal those as soon as its document loads instead of keeping a second
    // loading screen over it while the catalogue request and images settle.
    const isVisible = frameLoaded || phase === 'ready' || phase === 'timeout';
    const showOpeningScreen = phase === 'loading' && !frameLoaded;

    return (
        <main
            className="collection-page"
            style={{'--collection-site-header-height': `${headerHeight}px`}}
            aria-busy={phase === 'loading'}
        >
            <h1 className="collection-page__sr-only">Vieunite Collection</h1>
            <div className="collection-page__viewport">
                <iframe
                    key={attempt}
                    ref={iframeRef}
                    className={`collection-page__frame${isVisible ? ' collection-page__frame--visible' : ''}`}
                    src={collectionUrl}
                    title="Explore the Vieunite art collection"
                    loading="eager"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                    referrerPolicy="strict-origin-when-cross-origin"
                    onLoad={() => setFrameLoaded(true)}
                />

                {showOpeningScreen && (
                    <div className="collection-page__loading" role="status" aria-live="polite">
                        <div className="collection-page__loading-art" aria-hidden="true">
                            <span/>
                            <span/>
                            <span/>
                        </div>
                        <p>Opening the collection…</p>
                    </div>
                )}

                {phase === 'timeout' && (
                    <div className="collection-page__fallback" role="status" aria-live="polite">
                        <div>
                            <strong>The collection is taking longer than expected.</strong>
                            <span>You can keep browsing here or open it in a new tab.</span>
                        </div>
                        <div className="collection-page__fallback-actions">
                            <button type="button" onClick={retry}>Try again</button>
                            <a href={collectionUrl} target="_blank" rel="noopener noreferrer">
                                Open collection
                            </a>
                        </div>
                    </div>
                )}
            </div>
            <noscript>
                <p className="collection-page__noscript">
                    JavaScript is required for the embedded collection.{' '}
                    <a href={collectionUrl}>Open the Vieunite Collection</a>.
                </p>
            </noscript>
        </main>
    );
};

export default Collection;
